import { Link } from "react-router-dom";
import { Calendar, ArrowRight, BookOpen } from "lucide-react";
import Ticker from "@/components/Ticker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useBlogPosts } from "@/hooks/useBlog";
import { format } from "date-fns";
import blogExtensions from "@/assets/blog-extensions-guide.jpg";
import blogLaceFront from "@/assets/blog-lace-front-care.jpg";
import blogProtective from "@/assets/blog-protective-styles.jpg";
import blogBraidingGuide from "@/assets/blog-braiding-hair-guide.jpg";
import blogBraidCare from "@/assets/blog-braid-care.jpg";

const fallbackCovers: Record<string, string> = {
  "ultimate-guide-choosing-right-hair-extensions": blogExtensions,
  "how-to-care-for-lace-front-wig": blogLaceFront,
  "protective-hairstyles-why-they-matter-top-picks": blogProtective,
  "best-hair-for-braiding-complete-guide": blogBraidingGuide,
  "how-to-care-for-braids-expert-tips": blogBraidCare,
};

const BlogPage = () => {
  const { data: posts = [], isLoading } = useBlogPosts();

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Hair Care Blog"
        description="Expert hair care tips, styling guides, and advice on wigs, extensions, braids and protective hairstyles from the Plymhair team."
        url="/blog"
      />
      <Ticker />
      <Navbar />

      {/* Hero */}
      <section className="bg-foreground text-primary-foreground py-24 md:py-32 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <span className="inline-block text-[10px] font-body font-semibold uppercase tracking-[0.25em] text-primary bg-primary/10 px-3 py-1 rounded-full mb-4">
            <BookOpen className="w-3 h-3 inline-block mr-1 -mt-0.5" />
            Hair Care Tips
          </span>
          <h1 className="font-display text-5xl md:text-7xl mb-4">
            Our <span className="italic font-normal">Blog</span>
          </h1>
          <p className="font-body text-primary-foreground/60 text-lg max-w-md mx-auto">
            Expert advice on wigs, extensions, braids and natural hair care
          </p>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="flex-1 py-16">
        <div className="container max-w-5xl">
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[16/10] bg-muted rounded-xl mb-4" />
                  <div className="h-5 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-4 bg-muted rounded w-full" />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center text-muted-foreground py-20">No blog posts yet. Check back soon!</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, i) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group block animate-in fade-in slide-in-from-bottom-4 fill-mode-both"
                  style={{ animationDelay: `${i * 100}ms`, animationDuration: "500ms" }}
                >
                  <div className="aspect-[16/10] bg-gradient-to-br from-primary/20 to-accent/20 rounded-xl mb-4 overflow-hidden flex items-center justify-center">
                    {(post.cover_image || fallbackCovers[post.slug]) ? (
                      <img src={post.cover_image || fallbackCovers[post.slug]} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" width={1024} height={640} />
                    ) : (
                      <BookOpen className="w-12 h-12 text-primary/40" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground text-xs font-body mb-2">
                    <Calendar className="w-3 h-3" />
                    {format(new Date(post.created_at), "d MMM yyyy")}
                    <span className="mx-1">·</span>
                    {post.author}
                  </div>
                  <h2 className="font-display text-lg font-semibold mb-2 group-hover:text-primary transition-colors leading-snug">
                    {post.title}
                  </h2>
                  <p className="font-body text-sm text-muted-foreground line-clamp-2 mb-3">
                    {post.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-1 text-primary text-sm font-body font-medium group-hover:gap-2 transition-all">
                    Read more about {post.title} <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
